import * as React from "react";
import * as riskService from "../../../models/services/riskService";
import { IListFilterItem } from "../../../models/common/listFilterItem";
import { AppUtils } from "source/framework/appUtils";
import { Translations } from "ui/Scripts/source/models/translations";
import { Grid } from "@mui/material";
import { FilterSearchField } from "source/components/framework/Filters/FilterSearchField";
import { SaveDataObj } from "source/framework/saveData";
import { Base } from "ui/Scripts/source/framework/base";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { FilterButtons } from "source/components/framework/Filters/FilterButtons";
import { DrawerContent } from "source/components/framework/Drawer/DrawerContent";
import { PageFilterConditionDrawer } from "source/components/framework/Page/PageFilterConditionDrawer";
import { RiskStateIdFilterActive, IRiskPageFilterLists, IRiskPageFilterState, cloneRiskPageFilterState } from "./RiskPageUtils";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { IRiskSettingEdit } from "source/models/riskSetting/riskSettingEdit";

interface IRiskPageFilterDrawerProp extends IRiskPageFilterLists, IRiskPageFilterState {
    companyId: string;
    open: boolean;
    filter: IListFilterItem;
    canRemoveSharedFilters: boolean;
    stateIsReadOnly?: boolean;
    onSaveFilter: (saveAs: boolean, onGetGeneralFilter: () => any) => void;
    onRemoveFilter: (filterId: string) => void;
    onOk: (closeDialog: boolean, filterState: IRiskPageFilterState) => any;
    onCancel: () => void;
}

interface IRiskPageFilterDrawerState extends IRiskPageFilterState {
    settings: IRiskSettingEdit;
}

export class RiskPageFilterDrawer extends React.Component<IRiskPageFilterDrawerProp, IRiskPageFilterDrawerState> {
    private orgStateHash: string = "";

    getStateFromProps = (props: IRiskPageFilterDrawerProp): IRiskPageFilterDrawerState => {
        return {
            ...cloneRiskPageFilterState(props),
            settings: null
        };
    };

    constructor(props: IRiskPageFilterDrawerProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getSaveDataFromState(props, this.state).hash;
    }

    loadSettings = async () => {
        const settings = await AppUtils.callService(() => riskService.getRiskSettings(this.props.companyId), false);
        if (!settings) return;
        this.setState({
            settings
        });
    };

    componentDidUpdate(prevProps: IRiskPageFilterDrawerProp, prevState: IRiskPageFilterDrawerState): void {
        const props = this.props;
        if (!prevProps?.open && props.open) {
            this.loadSettings();
        }
        if (!prevProps ||
            (prevProps.filter?.getHash() === props.filter?.getHash() &&
            Base.equalStrArrays(prevProps.selectedRiskTypeIds, props.selectedRiskTypeIds) &&
            Base.equalStrArrays(prevProps.selectedUnitIds, props.selectedUnitIds) &&
            Base.equalStrArrays(prevProps.selectedStateIds, props.selectedStateIds) &&
            Base.equalStrArrays(prevProps.selectedRiskCategoryIds, props.selectedRiskCategoryIds) &&
            Base.equalStrArrays(prevProps.selectedRiskProcessIds, props.selectedRiskProcessIds) &&
            Base.equalStrArrays(prevProps.selectedRiskStandardIds, props.selectedRiskStandardIds) &&
            Base.equalStrArrays(prevProps.selectedRiskStakeholderIds, props.selectedRiskStakeholderIds) &&
            Base.equalStrArrays(prevProps.selectedResidualRiskIds, props.selectedResidualRiskIds) &&
            Base.equalStrArrays(prevProps.selectedRiskTreatmentStateIds, props.selectedRiskTreatmentStateIds) &&
            Base.equalStrArrays(prevProps.selectedHandlerUserCodes, props.selectedHandlerUserCodes) &&
            Base.equalStrArrays(prevProps.selectedOwners, props.selectedOwners))) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getSaveDataFromState(props, state).hash;
    }

    handleChangeRiskTypeIds = (selectedRiskTypeIds: string[]) => {
        this.setState({
            selectedRiskTypeIds
        });
    };

    handleChangeUnitIds = (selectedUnitIds: string[]) => {
        this.setState({
            selectedUnitIds
        });
    };

    handleChangeStateIds = (selectedStateIds: string[]) => {
        this.setState({
            selectedStateIds
        });
    };

    handleChangedResidualRiskIds = (selectedResidualRiskIds: string[]) => {
        this.setState({
            selectedResidualRiskIds
        });
    };

    handleChangedRiskTreatmentStateIds = (selectedRiskTreatmentStateIds: string[]) => {
        this.setState({
            selectedRiskTreatmentStateIds
        });
    };

    handleChangeRiskCategoryIds = (selectedRiskCategoryIds: string[]) => {
        this.setState({
            selectedRiskCategoryIds
        });
    };

    handleChangeRiskProcessIds = (selectedRiskProcessIds: string[]) => {
        this.setState({
            selectedRiskProcessIds
        });
    };

    handleChangeRiskStandardIds = (selectedRiskStandardIds: string[]) => {
        this.setState({
            selectedRiskStandardIds
        });
    };

    handleChangeRiskStakeholderIds = (selectedRiskStakeholderIds: string[]) => {
        this.setState({
            selectedRiskStakeholderIds
        });
    };

    handleChangeHandlerUserCodes = (selectedHandlerUserCodes: string[]) => {
        this.setState({
            selectedHandlerUserCodes
        });
    };

    handleChangeOwners = (selectedOwners: string[]) => {
        this.setState({
            selectedOwners
        });
    };

    getSaveDataFromState = (_props: IRiskPageFilterDrawerProp, state: IRiskPageFilterDrawerState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("selectedRiskTypeIds", state.selectedRiskTypeIds);
        result.append("selectedUnitIds", state.selectedUnitIds);
        result.append("selectedStateIds", state.selectedStateIds);
        result.append("selectedRiskCategoryIds", state.selectedRiskCategoryIds);
        result.append("selectedRiskProcessIds", state.selectedRiskProcessIds);
        result.append("selectedRiskStandardIds", state.selectedRiskStandardIds);
        result.append("selectedRiskStakeholderIds", state.selectedRiskStakeholderIds);
        result.append("selectedResidualRiskIds", state.selectedResidualRiskIds);
        result.append("selectedRiskTreatmentStateIds", state.selectedRiskTreatmentStateIds);
        result.append("selectedHandlerUserCodes", state.selectedHandlerUserCodes);
        result.append("selectedOwners", state.selectedOwners);
        return result;
    };

    static filterIsSet = (filterLists: IRiskPageFilterLists, filterState: IRiskPageFilterState): boolean => {
        return !(
            Base.equalStrArrays(filterState.selectedRiskTypeIds, [filterLists.riskTypes[0].id]) &&
            Base.equalStrArrays(filterState.selectedUnitIds, [filterLists.units[0].id]) &&
            Base.equalStrArrays(filterState.selectedStateIds, [filterLists.states[0].id]) &&
            Base.equalStrArrays(filterState.selectedRiskCategoryIds, [filterLists.riskCategories[0].id]) &&
            Base.equalStrArrays(filterState.selectedRiskProcessIds, [filterLists.riskProcesses[0].id]) &&
            Base.equalStrArrays(filterState.selectedRiskStandardIds, [filterLists.riskStandards[0].id]) &&
            Base.equalStrArrays(filterState.selectedRiskStakeholderIds, [filterLists.riskStakeholders[0].id]) &&
            Base.equalStrArrays(filterState.selectedResidualRiskIds, [filterLists.residualRisks[0].id]) &&
            Base.equalStrArrays(filterState.selectedRiskTreatmentStateIds, [filterLists.riskTreatmentStates[0].id]) &&
            Base.equalStrArrays(filterState.selectedHandlerUserCodes, [filterLists.handlers[0].id]) &&
            Base.equalStrArrays(filterState.selectedOwners, [filterLists.owners[0].id])
        );
    };

    handleSaveFilter = async (saveAs: boolean) => {
        const props = this.props;
        const state = this.state;
        if (!saveAs) {
            props.onOk(false, state);
            await this.props.onSaveFilter(false, null);
        } else {
            await this.props.onSaveFilter(true, () => {
                return props.onOk(false, state);
            });
        }
    };

    static getClearedParametersState = (props: IRiskPageFilterLists) => {
        return {
            selectedRiskTypeIds: [props.riskTypes[0].id],
            selectedUnitIds: [props.units[0].id],
            selectedStateIds: [props.states[0].id],
            selectedRiskCategoryIds: [props.riskCategories[0].id],
            selectedRiskProcessIds: [props.riskProcesses[0].id],
            selectedRiskStandardIds: [props.riskStandards[0].id],
            selectedRiskStakeholderIds: [props.riskStakeholders[0].id],
            selectedResidualRiskIds: [props.residualRisks[0].id],
            selectedRiskTreatmentStateIds: [props.riskTreatmentStates[0].id],
            selectedHandlerUserCodes: [props.handlers[0].id],
            selectedOwners: [props.owners[0].id],
        };
    };

    handleClear = () => {
        this.setState(
            RiskPageFilterDrawer.getClearedParametersState(this.props)
        );
    };

    handleSave = async () => {
        this.props.onOk(true, this.state);
    };

    handleCancel = async (checkChanges: boolean) => {
        AppUtils.cancelEditDialog(checkChanges, this.getSaveDataFromState(this.props, this.state).hash, this.orgStateHash, this.handleSave, this.props.onCancel, Translations.YouHaveUnapprovedChangesDoYouWantApproveChanges);
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.settings) return null;
        return (
            <PageFilterConditionDrawer
                open={props.open}
                onClose={() => this.handleCancel(false)}
            >
                <DrawerContent
                    content={
                        <>
                            {!!props.onSaveFilter && !!props.onRemoveFilter &&
                                <FilterSearchField
                                    filter={props.filter}
                                    canRemoveSharedFilters={props.canRemoveSharedFilters}
                                    onSaveFilter={props.onSaveFilter ? this.handleSaveFilter : null}
                                    onRemoveFilter={props.onRemoveFilter}
                                />
                            }
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.AType}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedRiskTypeIds}
                                            toggleAll={true}
                                            toggleAllId={Base.emptyGuid}
                                            values={props.riskTypes}
                                            onChange={this.handleChangeRiskTypeIds}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                    <FormField title={Translations.State}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedStateIds}
                                            toggleAll={true}
                                            toggleAllId={Base.emptyGuid}
                                            toggleSingleSelectIds={[RiskStateIdFilterActive]}
                                            values={props.states}
                                            onChange={this.handleChangeStateIds}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6} style={{ paddingTop: "0" }}>
                                    <FormField title={Translations.Unit}>
                                        <TreeCheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedUnitIds}
                                            values={props.units}
                                            onChange={this.handleChangeUnitIds} />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6} style={{ paddingTop: "0" }}>
                                    <FormField title={Translations.RiskCategory}>
                                        <TreeCheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedRiskCategoryIds}
                                            values={props.riskCategories}
                                            onChange={this.handleChangeRiskCategoryIds}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6} style={{ paddingTop: "0" }}>
                                    <FormField title={Translations.RiskOwner}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedOwners}
                                            toggleAll={true}
                                            values={props.owners}
                                            onChange={this.handleChangeOwners}
                                        />
                                    </FormField>
                                </Grid>
                                {!!state.settings?.riskStandardInUse &&
                                    <Grid item sm={6} style={{ paddingTop: "0" }}>
                                        <FormField title={Translations.RiskStandard}>
                                            <TreeCheckboxDropdown
                                                required={true}
                                                selectedIds={state.selectedRiskStandardIds}
                                                values={props.riskStandards}
                                                onChange={this.handleChangeRiskStandardIds}
                                            />
                                        </FormField>
                                    </Grid>
                                }
                                {!!state.settings?.riskStakeholderInUse &&
                                    <Grid item sm={6} style={{ paddingTop: "0" }}>
                                        <FormField title={Translations.RiskStakeholder}>
                                            <TreeCheckboxDropdown
                                                required={true}
                                                selectedIds={state.selectedRiskStakeholderIds}
                                                values={props.riskStakeholders}
                                                onChange={this.handleChangeRiskStakeholderIds}
                                            />
                                        </FormField>
                                    </Grid>
                                }
                                {!!state.settings?.riskProcessInUse &&
                                    <Grid item sm={6} style={{ paddingTop: "0" }}>
                                        <FormField title={state.settings.riskProcessName || Translations.RiskProcess}>
                                            <TreeCheckboxDropdown
                                                required={true}
                                                selectedIds={state.selectedRiskProcessIds}
                                                values={props.riskProcesses}
                                                onChange={this.handleChangeRiskProcessIds}
                                            />
                                        </FormField>
                                    </Grid>
                                }
                                <Grid item sm={6} style={{ paddingTop: "0" }}>
                                    <FormField title={Translations.ResidualRisk}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedResidualRiskIds}
                                            toggleAll={true}
                                            toggleAllId={Base.emptyGuid}
                                            values={props.residualRisks}
                                            onChange={this.handleChangedResidualRiskIds}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6} style={{ paddingTop: "0" }}>
                                    <FormField title={Translations.PersonInCharge}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedHandlerUserCodes}
                                            toggleAll={true}
                                            values={props.handlers}
                                            onChange={this.handleChangeHandlerUserCodes}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6} style={{ paddingTop: "0" }}>
                                    <FormField title={Translations.RiskTreatmentState}>
                                        <CheckboxDropdown
                                            required={true}
                                            selectedIds={state.selectedRiskTreatmentStateIds}
                                            toggleAll={true}
                                            toggleAllId={Base.emptyGuid}
                                            values={props.riskTreatmentStates}
                                            onChange={this.handleChangedRiskTreatmentStateIds}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        </>
                    }
                    actions={
                        <FilterButtons
                            onSave={this.handleSave}
                            onCancel={this.handleCancel}
                            onClear={this.handleClear}
                        />
                    }
                />
            </PageFilterConditionDrawer>
        );
    }
}
